import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Save,
  Upload,
  Trash2,
  Star,
  GripVertical,
  Loader2,
  ExternalLink,
  RefreshCw,
  Database,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* Resolve image URLs: backend-relative paths get prefixed. */
const resolveUrl = (url) => (url && url.startsWith('/api/') ? `${API}${url}` : url);

/* ───────────────────── Sortable Image Card ───────────────────── */
const SortableImage = ({ image, onDelete, onMakeHero, isHero, showHero }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: image.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`img-card-${image.id}`}
      className={`group relative aspect-[3/4] overflow-hidden rounded border-2 ${
        isHero ? 'border-amber-400' : 'border-zinc-800'
      } bg-zinc-900`}
    >
      <img
        src={resolveUrl(image.url)}
        alt=""
        className="w-full h-full object-cover pointer-events-none select-none"
        loading="lazy"
      />

      {/* Drag handle (whole card surface) */}
      <button
        {...attributes}
        {...listeners}
        className="absolute inset-0 cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
        data-testid={`drag-handle-${image.id}`}
      />

      {/* Top-left grip icon */}
      <div className="absolute top-1 left-1 bg-black/60 rounded p-1 opacity-0 group-hover:opacity-100 transition pointer-events-none">
        <GripVertical size={14} className="text-white/80" />
      </div>

      {/* Action buttons */}
      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
        {showHero && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMakeHero(image.id);
            }}
            title={isHero ? 'Current hero' : 'Make hero image'}
            className={`p-1.5 rounded ${
              isHero
                ? 'bg-amber-400 text-black'
                : 'bg-black/70 text-white hover:bg-amber-400 hover:text-black'
            }`}
            data-testid={`hero-btn-${image.id}`}
          >
            <Star size={13} fill={isHero ? 'currentColor' : 'none'} />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Delete this image?')) onDelete(image.id);
          }}
          title="Delete image"
          className="p-1.5 rounded bg-black/70 text-white hover:bg-red-500"
          data-testid={`delete-btn-${image.id}`}
        >
          <Trash2 size={13} />
        </button>
      </div>

      {isHero && (
        <div className="absolute bottom-1 left-1 bg-amber-400 text-black text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold pointer-events-none">
          Hero
        </div>
      )}
    </div>
  );
};

/* ───────────────────── Heading Inputs ───────────────────── */
const HeadingEditor = ({ heading, onChange, fields = ['label', 'title', 'subtitle'] }) => {
  const labels = {
    label: 'Eyebrow label (small caps)',
    title: 'Title (large)',
    subtitle: 'Subtitle',
    desc: 'Description',
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 p-3 bg-zinc-950/60 border border-zinc-800/70 rounded">
      {fields.map((f) => (
        <div key={f} className={f === 'desc' ? 'sm:col-span-2' : ''}>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {labels[f]}
          </label>
          {f === 'desc' ? (
            <textarea
              value={heading?.[f] || ''}
              onChange={(e) => onChange({ ...(heading || {}), [f]: e.target.value })}
              rows={2}
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400 resize-none"
              data-testid={`heading-${f}`}
            />
          ) : (
            <input
              type="text"
              value={heading?.[f] || ''}
              onChange={(e) => onChange({ ...(heading || {}), [f]: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
              data-testid={`heading-${f}`}
            />
          )}
        </div>
      ))}
    </div>
  );
};

/* ───────────────────── Section Wrapper ───────────────────── */
const SectionPanel = ({
  title,
  description,
  images,
  onReorder,
  onDelete,
  onUpload,
  onMakeHero,
  heroId,
  testid,
  heading,
  onHeadingChange,
  headingFields,
}) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = images.findIndex((i) => i.id === active.id);
    const newIdx = images.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(images, oldIdx, newIdx));
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${API}/api/portfolio/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          alert(`Upload failed: ${err.detail || res.statusText}`);
          break;
        }
        const data = await res.json();
        onUpload(data.url);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mb-10" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {description && (
            <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
          )}
          <p className="text-xs text-zinc-600 mt-1">
            {images.length} image{images.length !== 1 ? 's' : ''} · drag to reorder
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(Array.from(e.target.files))}
            data-testid={`file-input-${testid}`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-1.5 bg-white text-black text-xs uppercase tracking-wider rounded hover:bg-zinc-200 disabled:opacity-50"
            data-testid={`upload-btn-${testid}`}
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {onHeadingChange && (
          <HeadingEditor
            heading={heading}
            onChange={onHeadingChange}
            fields={headingFields}
          />
        )}
        <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {images.map((img) => (
              <SortableImage
                key={img.id}
                image={img}
                onDelete={onDelete}
                onMakeHero={onMakeHero}
                isHero={heroId === img.id}
                showHero={!!onMakeHero}
              />
            ))}
            {images.length === 0 && (
              <p className="col-span-full text-center text-zinc-600 text-sm py-8 border border-dashed border-zinc-800 rounded">
                No images yet — click Upload to add some.
              </p>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

/* ───────────────────── Shoot Editor ───────────────────── */
const ShootEditor = ({ shoot, onChange, onReorder, onDelete, onUpload, onMakeHero }) => {
  return (
    <div className="border border-zinc-800 rounded-lg p-4 mb-6 bg-zinc-950">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            Title
          </label>
          <input
            type="text"
            value={shoot.title || ''}
            onChange={(e) => onChange({ ...shoot, title: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
            data-testid={`shoot-title-${shoot.id}`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            Subtitle
          </label>
          <input
            type="text"
            value={shoot.subtitle || ''}
            onChange={(e) => onChange({ ...shoot, subtitle: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
            data-testid={`shoot-subtitle-${shoot.id}`}
          />
        </div>
      </div>
      <SectionPanel
        title={`Images`}
        description="Click ★ to set as the large hero image on the left of this cluster."
        images={shoot.images}
        heroId={shoot.heroId}
        onReorder={(newImgs) => onChange({ ...shoot, images: newImgs })}
        onDelete={(imgId) => onDelete(imgId)}
        onUpload={(url) => onUpload(url)}
        onMakeHero={(imgId) => onMakeHero(imgId)}
        testid={`shoot-${shoot.id}`}
      />
    </div>
  );
};

/* ═══════════════════════ Main Page ═══════════════════════ */
export default function AdminPortfolioEditor() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/portfolio/content`)
      .then((r) => r.json())
      .then((d) => {
        setDoc(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const update = useCallback((producer) => {
    setDoc((d) => {
      if (!d) return d;
      const next = JSON.parse(JSON.stringify(d));
      producer(next);
      return next;
    });
    setDirty(true);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/portfolio/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(doc),
      });
      if (res.status === 409) {
        // Version conflict — someone else (or another tab/chat) saved newer data.
        const conflict = await res.json().catch(() => ({}));
        const reload = window.confirm(
          (conflict?.detail?.message ||
            'The portfolio was changed elsewhere since you opened this editor.') +
            '\n\nClick OK to reload the latest version. Your unsaved edits in this tab will be lost.'
        );
        if (reload) {
          window.location.reload();
        }
        return;
      }
      if (!res.ok) {
        alert('Save failed. Check your login.');
        return;
      }
      const result = await res.json().catch(() => ({}));
      if (result.version) {
        // Keep our local doc's version in sync so subsequent saves don't conflict
        setDoc((d) => (d ? { ...d, version: result.version } : d));
      }
      setDirty(false);
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  };

  const reseed = async () => {
    const ok = window.confirm(
      'This will REPLACE the current portfolio with the captured snapshot ' +
        '(every section, every image order, every title).\n\n' +
        'Use this ONLY to sync production with the latest preview state. ' +
        'Continue?'
    );
    if (!ok) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/portfolio/reseed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        alert('Reseed failed. Check your login.');
        return;
      }
      const result = await res.json();
      const c = result.counts || {};
      alert(
        `✓ Synced from snapshot.\n` +
          `Main shots: ${c.mainShots}  ·  Profiles: ${c.profiles}\n` +
          `Shoots: ${c.shoots}  ·  Statement: ${c.statement}\n` +
          `Sky High: ${c.skyHighImages}  ·  Movie Frames: ${c.movieFrames}\n\n` +
          `Refreshing editor…`
      );
      window.location.reload();
    } catch (e) {
      alert('Reseed error: ' + e.message);
    }
  };

  /* Helpers — simple sections */
  const setSimpleImages = (key, imgs) => update((d) => (d[key] = imgs));
  const deleteFromSimple = (key, imgId) =>
    update((d) => (d[key] = d[key].filter((i) => i.id !== imgId)));
  const appendToSimple = (key, url) =>
    update((d) => d[key].push({ id: crypto.randomUUID().replace(/-/g, ''), url }));

  /* Helpers — shoots */
  const updateShoot = (idx, next) => update((d) => (d.shoots[idx] = next));
  const deleteShootImage = (idx, imgId) =>
    update((d) => {
      const s = d.shoots[idx];
      s.images = s.images.filter((i) => i.id !== imgId);
      if (s.heroId === imgId && s.images.length) s.heroId = s.images[0].id;
    });
  const appendShootImage = (idx, url) =>
    update((d) => {
      const id = crypto.randomUUID().replace(/-/g, '');
      d.shoots[idx].images.push({ id, url });
      if (!d.shoots[idx].heroId) d.shoots[idx].heroId = id;
    });
  const setShootHero = (idx, imgId) => update((d) => (d.shoots[idx].heroId = imgId));

  /* Helpers — skyHigh */
  const setSkyImages = (imgs) => update((d) => (d.skyHigh.images = imgs));
  const deleteSkyImage = (imgId) =>
    update((d) => {
      d.skyHigh.images = d.skyHigh.images.filter((i) => i.id !== imgId);
      if (d.skyHigh.heroId === imgId && d.skyHigh.images.length)
        d.skyHigh.heroId = d.skyHigh.images[0].id;
    });
  const appendSkyImage = (url) =>
    update((d) => {
      const id = crypto.randomUUID().replace(/-/g, '');
      d.skyHigh.images.push({ id, url });
      if (!d.skyHigh.heroId) d.skyHigh.heroId = id;
    });
  const setSkyHero = (imgId) => update((d) => (d.skyHigh.heroId = imgId));

  /* Helpers — section headings */
  const updateHeading = (sectionKey, next) =>
    update((d) => {
      if (!d.headings) d.headings = {};
      d.headings[sectionKey] = next;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        Failed to load portfolio.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-black/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-1 text-zinc-400 hover:text-white text-xs"
              data-testid="back-to-admin"
            >
              <ArrowLeft size={14} /> Admin
            </button>
            <span className="text-zinc-700">·</span>
            <h1 className="text-base md:text-lg font-semibold">Portfolio Editor</h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/portfolio"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
              data-testid="preview-link"
            >
              Preview <ExternalLink size={12} />
            </a>
            <button
              onClick={reseed}
              className="text-xs text-zinc-400 hover:text-amber-400 flex items-center gap-1"
              title="Replace the current portfolio with the captured snapshot (one-time sync)"
              data-testid="reseed-btn"
            >
              <Database size={12} /> Sync from snapshot
            </button>
            <button
              onClick={() => {
                if (
                  !dirty ||
                  window.confirm(
                    'You have unsaved changes. Refresh and discard them?'
                  )
                ) {
                  window.location.reload();
                }
              }}
              className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
              title="Reload the latest version from the server"
              data-testid="refresh-btn"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            {savedAt && !dirty && (
              <span className="text-xs text-emerald-400">
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
            {dirty && <span className="text-xs text-amber-400">Unsaved changes</span>}
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-black text-xs uppercase tracking-wider rounded font-semibold hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <p className="text-zinc-400 text-sm mb-8 max-w-3xl">
          Drag thumbnails to reorder. Hover any image to delete it or mark it as the hero (★).
          Click <strong>Save</strong> at the top to publish your changes — the public{' '}
          <code className="text-zinc-300">/portfolio</code> page updates instantly.
        </p>

        <SectionPanel
          title="Main Casting Shots"
          description="Top of the page — the first three images casting directors see."
          images={doc.mainShots}
          onReorder={(imgs) => setSimpleImages('mainShots', imgs)}
          onDelete={(id) => deleteFromSimple('mainShots', id)}
          onUpload={(url) => appendToSimple('mainShots', url)}
          testid="section-main-shots"
        />

        <SectionPanel
          title="Profiles — From Every Angle"
          description="The three-profile row directly below the casting shots."
          images={doc.profiles}
          onReorder={(imgs) => setSimpleImages('profiles', imgs)}
          onDelete={(id) => deleteFromSimple('profiles', id)}
          onUpload={(url) => appendToSimple('profiles', url)}
          heading={doc.headings?.profiles}
          onHeadingChange={(next) => updateHeading('profiles', next)}
          headingFields={['label', 'title']}
          testid="section-profiles"
        />

        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-1">Shoots</h2>
          <p className="text-zinc-500 text-xs mb-4">
            Each shoot is one cluster on the public page. Edit title/subtitle, mark a hero image
            with ★, drag to reorder, and upload new shots.
          </p>

          {/* Shoots parent heading (rendered above the 5 shoot clusters on the public page) */}
          <div className="mb-4 border border-zinc-800/70 rounded p-3 bg-zinc-950">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
              Section heading (appears above all shoots)
            </p>
            <HeadingEditor
              heading={doc.headings?.shoots}
              onChange={(next) => updateHeading('shoots', next)}
              fields={['label', 'title', 'desc']}
            />
          </div>

          {doc.shoots.map((shoot, idx) => (
            <ShootEditor
              key={shoot.id}
              shoot={shoot}
              onChange={(next) => updateShoot(idx, next)}
              onReorder={(imgs) => updateShoot(idx, { ...shoot, images: imgs })}
              onDelete={(imgId) => deleteShootImage(idx, imgId)}
              onUpload={(url) => appendShootImage(idx, url)}
              onMakeHero={(imgId) => setShootHero(idx, imgId)}
            />
          ))}
        </div>

        <SectionPanel
          title="The Statement"
          description="Three classic, elegant images mid-page."
          images={doc.statement}
          onReorder={(imgs) => setSimpleImages('statement', imgs)}
          onDelete={(id) => deleteFromSimple('statement', id)}
          onUpload={(url) => appendToSimple('statement', url)}
          heading={doc.headings?.statement}
          onHeadingChange={(next) => updateHeading('statement', next)}
          headingFields={['title', 'subtitle']}
          testid="section-statement"
        />

        <div className="border border-zinc-800 rounded-lg p-4 mb-10 bg-zinc-950">
          <h2 className="text-lg font-semibold mb-3">Sky High</h2>
          <SectionPanel
            title="Rooftop Cluster"
            description="Hero + thumbnail row. Mark one image with ★ as the big hero."
            images={doc.skyHigh.images}
            heroId={doc.skyHigh.heroId}
            onReorder={setSkyImages}
            onDelete={deleteSkyImage}
            onUpload={appendSkyImage}
            onMakeHero={setSkyHero}
            heading={doc.headings?.skyHigh}
            onHeadingChange={(next) => updateHeading('skyHigh', next)}
            headingFields={['title', 'subtitle']}
            testid="section-skyhigh"
          />
        </div>

        <SectionPanel
          title="Movie Frames — Scene by Scene"
          description="The 20-frame film-strip at the bottom of the page."
          images={doc.movieFrames}
          onReorder={(imgs) => setSimpleImages('movieFrames', imgs)}
          onDelete={(id) => deleteFromSimple('movieFrames', id)}
          onUpload={(url) => appendToSimple('movieFrames', url)}
          heading={doc.headings?.movieFrames}
          onHeadingChange={(next) => updateHeading('movieFrames', next)}
          headingFields={['label', 'title', 'desc']}
          testid="section-movie-frames"
        />
      </div>

      {/* Floating save button on mobile */}
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="md:hidden fixed bottom-5 right-5 z-40 flex items-center gap-2 px-5 py-3 bg-amber-400 text-black text-xs uppercase tracking-wider rounded-full font-semibold shadow-lg shadow-amber-500/30"
          data-testid="save-btn-mobile"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      )}
    </div>
  );
}
